// src/components/mitchRoom/modelOutputs/PlayerAvailabilityChart.js
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Brand chart palette (matches CHART_COLORS hues used across the app)
const COLORS = {
  Started: '#59A14F',
  NotStarted: '#E15759',
  Played: '#4E79A7',
  NotPlayed: '#F28E2B',
};

// Bucket size in percentage points
const BUCKET_SIZE = 10;

// Brier score + ECE for a binary probability model, computed in a single pass.
// Records supplies [prob (0-1), outcome (0/1)] pairs; ECE buckets follow the
// same 10% rounding as the charts.
function computeCalibrationMetrics(records) {
  let n = 0;
  let brierSum = 0;
  const buckets = new Map(); // bucket -> { sumProb, hits, count }

  records.forEach(([prob, outcome]) => {
    brierSum += (prob - outcome) ** 2;
    n++;

    const bucket = Math.max(0, Math.min(100, Math.round((prob * 100) / BUCKET_SIZE) * BUCKET_SIZE));
    const entry = buckets.get(bucket) || { sumProb: 0, hits: 0, count: 0 };
    entry.sumProb += prob;
    entry.hits += outcome;
    entry.count++;
    buckets.set(bucket, entry);
  });

  if (n === 0) return null;

  let ece = 0;
  buckets.forEach(({ sumProb, hits, count }) => {
    ece += Math.abs(hits / count - sumProb / count) * (count / n);
  });

  return { brier: brierSum / n, ece, n };
}

function MetricCards({ metrics }) {
  if (!metrics) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-3">
      <div className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
        <p className="text-token-base text-muted-foreground">Brier Score</p>
        <p className="text-token-2xl font-semibold">{metrics.brier.toFixed(3)}</p>
        <p className="text-token-xs text-muted-foreground">Lower is better (0 = perfect)</p>
      </div>
      <div className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
        <p className="text-token-base text-muted-foreground">ECE</p>
        <p className="text-token-2xl font-semibold">{(metrics.ece * 100).toFixed(1)}%</p>
        <p className="text-token-xs text-muted-foreground">
          Expected calibration error over {metrics.n.toLocaleString()} records
        </p>
      </div>
    </div>
  );
}

function StatCards({ stats, total, labels, colors }) {
  return (
    <div className="mb-3 flex flex-wrap gap-3">
      {stats.map((stat) => (
        <div key={stat.result} className="min-w-[120px] rounded-token-medium border border-borderGrey p-3">
          <p className="text-token-md font-semibold" style={{ color: colors[stat.result] }}>
            {labels[stat.result]}
          </p>
          <p className="text-token-sm">
            Count: {stat.count.toLocaleString()} (
            {total > 0 ? ((stat.count / total) * 100).toFixed(1) : 0}%)
          </p>
          {stat.count > 0 && stat.mean != null && stat.median != null && (
            <>
              <p className="text-token-sm">Mean: {stat.mean.toFixed(1)}%</p>
              <p className="text-token-sm">Median: {stat.median.toFixed(1)}%</p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PlayerAvailabilityChart({
  availabilityData,
}) {
  // The endpoint already filters for historical data (actual_started IS NOT NULL)
  // Just ensure we have valid data with actual results
  const filteredAvailability = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) return [];

    // Filter to records with actual results for calibration
    return availabilityData.filter((record) => {
      return record.actual_started != null || record.actual_played != null;
    });
  }, [availabilityData]);

  // Transform data into 10% buckets for STARTING probability
  const startingChartData = useMemo(() => {
    const buckets = {};
    for (let i = 0; i <= 100; i += BUCKET_SIZE) {
      buckets[i] = { bucket: i, Started: 0, NotStarted: 0 };
    }

    filteredAvailability.forEach((record) => {
      if (record.prob_starting == null || record.actual_started == null) return;

      const probStartPct = (record.prob_starting || 0) * 100;
      const roundedBucket = Math.round(probStartPct / BUCKET_SIZE) * BUCKET_SIZE;
      const bucket = Math.max(0, Math.min(100, roundedBucket));

      if (record.actual_started === 1) {
        buckets[bucket].Started++;
      } else {
        buckets[bucket].NotStarted++;
      }
    });

    // Filter out empty bins (where both Started and NotStarted are 0)
    return Object.values(buckets).filter(
      (b) => b.Started > 0 || b.NotStarted > 0
    );
  }, [filteredAvailability]);

  // Transform data into 10% buckets for PLAYING probability
  const playingChartData = useMemo(() => {
    const buckets = {};
    for (let i = 0; i <= 100; i += BUCKET_SIZE) {
      buckets[i] = { bucket: i, Played: 0, NotPlayed: 0 };
    }

    filteredAvailability.forEach((record) => {
      if (record.prob_playing == null || record.actual_played == null) return;

      const probPlayPct = (record.prob_playing || 0) * 100;
      const roundedBucket = Math.round(probPlayPct / BUCKET_SIZE) * BUCKET_SIZE;
      const bucket = Math.max(0, Math.min(100, roundedBucket));

      if (record.actual_played === 1) {
        buckets[bucket].Played++;
      } else {
        buckets[bucket].NotPlayed++;
      }
    });

    // Filter out empty bins (where both Played and NotPlayed are 0)
    return Object.values(buckets).filter(
      (b) => b.Played > 0 || b.NotPlayed > 0
    );
  }, [filteredAvailability]);

  // Calculate stats for STARTING predictions
  const startingStats = useMemo(() => {
    const groups = { Started: [], NotStarted: [] };

    filteredAvailability.forEach((record) => {
      if (record.prob_starting == null || record.actual_started == null) return;

      const probStartPct = (record.prob_starting || 0) * 100;
      if (record.actual_started === 1) {
        groups.Started.push(probStartPct);
      } else {
        groups.NotStarted.push(probStartPct);
      }
    });

    return Object.entries(groups).map(([result, values]) => {
      if (values.length === 0) {
        return { result, count: 0, mean: null, median: null };
      }

      const sorted = [...values].sort((a, b) => a - b);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const median = sorted[Math.floor(sorted.length / 2)];

      return { result, count: values.length, mean, median };
    });
  }, [filteredAvailability]);

  // Calculate stats for PLAYING predictions
  const playingStats = useMemo(() => {
    const groups = { Played: [], NotPlayed: [] };

    filteredAvailability.forEach((record) => {
      if (record.prob_playing == null || record.actual_played == null) return;

      const probPlayPct = (record.prob_playing || 0) * 100;
      if (record.actual_played === 1) {
        groups.Played.push(probPlayPct);
      } else {
        groups.NotPlayed.push(probPlayPct);
      }
    });

    return Object.entries(groups).map(([result, values]) => {
      if (values.length === 0) {
        return { result, count: 0, mean: null, median: null };
      }

      const sorted = [...values].sort((a, b) => a - b);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const median = sorted[Math.floor(sorted.length / 2)];

      return { result, count: values.length, mean, median };
    });
  }, [filteredAvailability]);

  // Headline calibration metrics (Brier score + ECE), one scalar per model
  // output so improvement can be tracked across model runs.
  const startingMetrics = useMemo(
    () =>
      computeCalibrationMetrics(
        filteredAvailability
          .filter((r) => r.prob_starting != null && r.actual_started != null)
          .map((r) => [r.prob_starting || 0, r.actual_started === 1 ? 1 : 0])
      ),
    [filteredAvailability]
  );

  const playingMetrics = useMemo(
    () =>
      computeCalibrationMetrics(
        filteredAvailability
          .filter((r) => r.prob_playing != null && r.actual_played != null)
          .map((r) => [r.prob_playing || 0, r.actual_played === 1 ? 1 : 0])
      ),
    [filteredAvailability]
  );

  const totalStarting = startingStats.reduce((acc, s) => acc + s.count, 0);
  const totalPlaying = playingStats.reduce((acc, s) => acc + s.count, 0);

  // Calculate max values for each axis (use Math.ceil to avoid floating point decimals)
  const startingMaxStarted = Math.ceil(Math.max(...startingChartData.map((d) => d.Started), 1) * 1.1);
  const startingMaxNotStarted = Math.ceil(Math.max(...startingChartData.map((d) => d.NotStarted), 1) * 1.1);
  const playingMaxPlayed = Math.ceil(Math.max(...playingChartData.map((d) => d.Played), 1) * 1.1);
  const playingMaxNotPlayed = Math.ceil(Math.max(...playingChartData.map((d) => d.NotPlayed), 1) * 1.1);

  if (totalStarting === 0 && totalPlaying === 0) {
    return (
      <div className="p-6 text-center text-token-md text-muted-foreground">
        No historical availability data available.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* STARTING PROBABILITY SECTION */}
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Starting Probability Calibration</h3>
        <p className="mb-3 text-token-base text-muted-foreground">
          Shows how predicted starting probabilities correlate with whether players actually
          started. Green bars (left axis) = Started, Red bars (right axis) = Not Started.
        </p>

        <MetricCards metrics={startingMetrics} />

        <StatCards
          stats={startingStats}
          total={totalStarting}
          labels={{ Started: 'Started', NotStarted: 'Not Started' }}
          colors={COLORS}
        />

        {/* Dual Y-Axis Grouped Bar Chart */}
        {startingChartData.length > 0 && (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={startingChartData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis
                dataKey="bucket"
                tickFormatter={(value) => `${value}%`}
                label={{ value: 'Predicted Start %', position: 'insideBottom', offset: -10 }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke={COLORS.Started}
                domain={[0, startingMaxStarted]}
                allowDecimals={false}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Started', angle: -90, position: 'insideLeft', fill: COLORS.Started }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={COLORS.NotStarted}
                domain={[0, startingMaxNotStarted]}
                allowDecimals={false}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Not Started', angle: 90, position: 'insideRight', fill: COLORS.NotStarted }}
              />
              <Tooltip
                formatter={(value, name) => [value.toLocaleString(), name === 'Started' ? 'Started' : 'Not Started']}
                labelFormatter={(label) => `Start Probability: ${label}%`}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="Started" fill={COLORS.Started} name="Started" isAnimationActive={false} />
              <Bar yAxisId="right" dataKey="NotStarted" fill={COLORS.NotStarted} name="Not Started" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* PLAYING PROBABILITY SECTION */}
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Playing Probability Calibration</h3>
        <p className="mb-3 text-token-base text-muted-foreground">
          Shows how predicted playing probabilities correlate with whether players actually played
          any minutes. Blue bars (left axis) = Played, Orange bars (right axis) = Not Played.
        </p>

        <MetricCards metrics={playingMetrics} />

        <StatCards
          stats={playingStats}
          total={totalPlaying}
          labels={{ Played: 'Played', NotPlayed: 'Not Played' }}
          colors={COLORS}
        />

        {/* Dual Y-Axis Grouped Bar Chart */}
        {playingChartData.length > 0 && (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={playingChartData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis
                dataKey="bucket"
                tickFormatter={(value) => `${value}%`}
                label={{ value: 'Predicted Play %', position: 'insideBottom', offset: -10 }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke={COLORS.Played}
                domain={[0, playingMaxPlayed]}
                allowDecimals={false}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Played', angle: -90, position: 'insideLeft', fill: COLORS.Played }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={COLORS.NotPlayed}
                domain={[0, playingMaxNotPlayed]}
                allowDecimals={false}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Not Played', angle: 90, position: 'insideRight', fill: COLORS.NotPlayed }}
              />
              <Tooltip
                formatter={(value, name) => [value.toLocaleString(), name === 'Played' ? 'Played' : 'Not Played']}
                labelFormatter={(label) => `Play Probability: ${label}%`}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="Played" fill={COLORS.Played} name="Played" isAnimationActive={false} />
              <Bar yAxisId="right" dataKey="NotPlayed" fill={COLORS.NotPlayed} name="Not Played" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
